import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://linafqwmulbsbovmawgu.supabase.co';
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'sb_publishable_2NnoeKXCFVLQ7z-RqHf_hQ_c4uDan-x';

export class AuthService {
  constructor() {
    this.client = createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
    });
    this.session = null;
    this.saveTimer = 0;
    this.recoveryMode = false;
  }

  get user() { return this.session?.user || null; }
  get email() { return this.user?.email || ''; }

  async init() {
    this.recoveryMode = window.location.hash.includes('type=recovery') || new URLSearchParams(window.location.search).get('type') === 'recovery';
    const { data, error } = await this.client.auth.getSession();
    if (error) throw error;
    this.session = data.session;
    this.client.auth.onAuthStateChange((event, session) => {
      this.session = session;
      if (session?.access_token) this.client.realtime.setAuth(session.access_token).catch(() => {});
      if (event === 'PASSWORD_RECOVERY') this.recoveryMode = true;
    });
    return this.session;
  }

  async signIn(email, password) {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    this.session = data.session;
    return data;
  }

  async signUp(email, password, displayName) {
    const { data, error } = await this.client.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { display_name: displayName }
      }
    });
    if (error) throw error;
    this.session = data.session;
    return data;
  }

  async signOut() {
    window.clearTimeout(this.saveTimer);
    const { error } = await this.client.auth.signOut();
    if (error) throw error;
    this.session = null;
  }

  async resetPassword(email) {
    const { error } = await this.client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`
    });
    if (error) throw error;
  }

  async updatePassword(password) {
    const { data, error } = await this.client.auth.updateUser({ password });
    if (error) throw error;
    this.recoveryMode = false;
    return data;
  }

  async loadOrCreate(localState) {
    if (!this.user) return null;
    const { data, error } = await this.client
      .from('clash_player_saves')
      .select('game_state, updated_at')
      .eq('user_id', this.user.id)
      .maybeSingle();
    if (error) throw error;
    if (data?.game_state && Object.keys(data.game_state).length) return data.game_state;
    await this.save(localState);
    return null;
  }

  async save(state) {
    if (!this.user) return null;
    const snapshot = JSON.parse(JSON.stringify(state));
    const { data, error } = await this.client
      .from('clash_player_saves')
      .upsert({
        user_id: this.user.id,
        display_name: String(state.playerName || 'Stamp').slice(0, 20),
        game_state: snapshot,
        save_version: 3,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' })
      .select('updated_at')
      .single();
    if (error) throw error;
    return data;
  }

  queueSave(state) {
    if (!this.user) return;
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.save(state).catch(() => {}), 1400);
  }
}
